// src/pages/TripPlanner/components/ItineraryGenerator.tsx
import React, { useState } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  CircularProgress, 
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Chip,
  Grid,
  InputAdornment
} from '@mui/material';
import { 
  AutoAwesome as MagicIcon,
  Schedule as ScheduleIcon,
  DirectionsCar as TransportIcon,
  Hotel as HotelIcon,
  Restaurant as RestaurantIcon,
  Attractions as AttractionIcon,
  AddCircle as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Refresh as RefreshIcon,
  Shuffle as ShuffleIcon
} from '@mui/icons-material';
import { format, addDays, isSameDay } from 'date-fns';

// Types
interface TripDetails {
  id?: string;
  title: string;
  description: string;
  destination: string;
  startDate: Date | null;
  endDate: Date | null;
  purpose: string;
  style: string;
  pace: string;
  budget: number[];
  groupSize: number;
  groupRelationship: string;
  interests: string[];
  [key: string]: any;
}

interface ItineraryDay {
  date: Date;
  activities: ItineraryActivity[];
}

interface ItineraryActivity {
  id: string;
  title: string;
  description: string;
  time: string;
  location: string;
  type: 'activity' | 'food' | 'accommodation' | 'transportation';
  cost?: number;
  duration?: string;
  notes?: string;
  isCustom?: boolean;
}

interface ItineraryGeneratorProps {
  tripDetails: TripDetails;
  onSaveItinerary: (itinerary: ItineraryDay[]) => void;
  onAddActivity: (activity: ItineraryActivity, day: Date) => void;
  existingItinerary?: ItineraryDay[];
}

const ItineraryGenerator: React.FC<ItineraryGeneratorProps> = ({
  tripDetails,
  onSaveItinerary,
  onAddActivity,
  existingItinerary = []
}) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [itinerary, setItinerary] = useState<ItineraryDay[]>(existingItinerary);
  const [currentDayIndex, setCurrentDayIndex] = useState(0);
  const [editActivity, setEditActivity] = useState<ItineraryActivity | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [activityType, setActivityType] = useState<string>('all');
  
  // Generate days between start and end dates
  const generateDays = (): Date[] => {
    if (!tripDetails.startDate || !tripDetails.endDate) return [];
    
    const days: Date[] = [];
    let currentDate = new Date(tripDetails.startDate);
    
    while (currentDate <= tripDetails.endDate) {
      days.push(new Date(currentDate));
      currentDate = addDays(currentDate, 1);
    }
    
    return days;
  };
  
  // Initialize itinerary with empty days if not already set
  const initializeItinerary = () => {
    if (itinerary.length > 0) return itinerary;
    
    const days = generateDays();
    return days.map(date => ({
      date,
      activities: []
    }));
  };

  // Generate trip itinerary using AI
  const generateItinerary = async () => {
     setGenerating(true);

     try {
        // In a real implementation, you would call your AI service here
        // For now, we'll simulate a response with sample data
        const days = generateDays();

        // Create prompt based on trip details
        const prompt = `
        Create a ${days.length}-day itinerary for a trip to ${tripDetails.destination}.
        Trip type: ${tripDetails.purpose}, Travel style: ${tripDetails.style}, Pace: ${tripDetails.pace}
        Budget range: $${tripDetails.budget[0]}-$${tripDetails.budget[1]} per day
        Group: ${tripDetails.groupSize} people (${tripDetails.groupRelationship})
        Interests: ${tripDetails.interests.join(', ')}
        Special notes: ${tripDetails.additionalNotes || 'None'}
      `;

        // Simulate API delay
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Generate sample itinerary based on trip details
        const generatedItinerary = generateSampleItinerary(days);

        setItinerary(generatedItinerary);
        setGenerating(false);
     } catch (error) {
        console.error('Error generating itinerary:', error);
        setGenerating(false);
     }
  };

  // Generate a sample itinerary (this would be replaced by actual AI response)
  const generateSampleItinerary = (days: Date[]): ItineraryDay[] => {
     // Get random activities based on destination
     const activities = getSampleActivities(tripDetails.destination);

     // Distribute activities across days
     return days.map((date, index) => {
        const dayActivities = [];
        const dayIndex = index % activities.length;

        // Add morning activity
        dayActivities.push({
           ...activities[dayIndex].morning,
           id: `morning-${index}`,
           time: '09:00 AM',
        });

        // Add lunch
        dayActivities.push({
           ...activities[dayIndex].lunch,
           id: `lunch-${index}`,
           time: '12:30 PM',
        });

        // Add afternoon activity
        dayActivities.push({
           ...activities[dayIndex].afternoon,
           id: `afternoon-${index}`,
           time: '02:30 PM',
        });

        // Add dinner
        dayActivities.push({
           ...activities[dayIndex].dinner,
           id: `dinner-${index}`,
           time: '07:00 PM',
        });

        return {
           date,
           activities: dayActivities,
        };
     });
  };

  // Get sample activities based on destination (would be replaced by AI suggestions)
  const getSampleActivities = (destination: string) => {
     if (destination.toLowerCase().includes('paris')) {
        return [
           {
              morning: {
                 title: 'Eiffel Tower Visit',
                 description:
                    'Start your day with an iconic visit to the Eiffel Tower. Enjoy panoramic views of Paris from the top.',
                 location: 'Champ de Mars, 5 Avenue Anatole France',
                 type: 'activity' as const,
                 cost: 25,
                 duration: '2 hours',
              },
              lunch: {
                 title: 'Lunch at Café de Flore',
                 description:
                    'Enjoy a classic Parisian lunch at this historic café loved by artists and intellectuals.',
                 location: '172 Boulevard Saint-Germain',
                 type: 'food' as const,
                 cost: 35,
                 duration: '1.5 hours',
              },
              afternoon: {
                 title: 'Louvre Museum Tour',
                 description:
                    "Explore the world's largest art museum and see masterpieces like the Mona Lisa and Venus de Milo.",
                 location: 'Rue de Rivoli, 75001',
                 type: 'activity' as const,
                 cost: 15,
                 duration: '3 hours',
              },
              dinner: {
                 title: 'Dinner at Le Petit Nice',
                 description: 'Enjoy authentic French cuisine in a cozy bistro setting.',
                 location: '14 Rue Saint-Denis',
                 type: 'food' as const,
                 cost: 50,
                 duration: '2 hours',
              },
           },
           {
              morning: {
                 title: 'Notre-Dame Cathedral Area',
                 description:
                    'Visit the area around the historic Notre-Dame Cathedral and explore the Île de la Cité.',
                 location: 'Île de la Cité',
                 type: 'activity' as const,
                 cost: 0,
                 duration: '2 hours',
              },
              lunch: {
                 title: 'Lunch at Les Deux Magots',
                 description:
                    'Experience another famous Parisian café with excellent people-watching opportunities.',
                 location: '6 Place Saint-Germain des Prés',
                 type: 'food' as const,
                 cost: 30,
                 duration: '1.5 hours',
              },
              afternoon: {
                 title: 'Montmartre Walking Tour',
                 description:
                    "Explore the charming artists' district of Montmartre and visit Sacré-Cœur Basilica.",
                 location: 'Montmartre, 75018',
                 type: 'activity' as const,
                 cost: 0,
                 duration: '3 hours',
              },
              dinner: {
                 title: 'Dinner at Au Pied de Cochon',
                 description:
                    'Try traditional French onion soup and other classics at this historic restaurant.',
                 location: '6 Rue Coquillière',
                 type: 'food' as const,
                 cost: 45,
                 duration: '2 hours',
              },
           },
           {
              morning: {
                 title: 'Seine River Cruise',
                 description:
                    'Take a relaxing cruise along the Seine to see Paris from a different perspective.',
                 location: "Pont de l'Alma",
                 type: 'activity' as const,
                 cost: 15,
                 duration: '1 hour',
              },
              lunch: {
                 title: 'Picnic at Jardin du Luxembourg',
                 description: "Enjoy a picnic lunch in one of Paris's most beautiful gardens.",
                 location: 'Jardin du Luxembourg',
                 type: 'food' as const,
                 cost: 15,
                 duration: '1.5 hours',
              },
              afternoon: {
                 title: "Musée d'Orsay Visit",
                 description:
                    'Explore the impressive collection of Impressionist and Post-Impressionist masterpieces.',
                 location: "1 Rue de la Légion d'Honneur",
                 type: 'activity' as const,
                 cost: 14,
                 duration: '2.5 hours',
              },
              dinner: {
                 title: "Dinner at Le Relais de l'Entrecôte",
                 description:
                    'Experience the famous steak-frites restaurant with its secret sauce.',
                 location: '20 Rue Saint-Benoît',
                 type: 'food' as const,
                 cost: 40,
                 duration: '1.5 hours',
              },
           },
        ];
     } else {
        // Generic itinerary for other destinations
        return [
           {
              morning: {
                 title: 'City Walking Tour',
                 description:
                    'Explore the main attractions of the city center with a guided walking tour.',
                 location: 'City Center',
                 type: 'activity' as const,
                 cost: 20,
                 duration: '2.5 hours',
              },
              lunch: {
                 title: 'Lunch at Local Restaurant',
                 description: 'Try authentic local cuisine at a popular restaurant.',
                 location: 'Downtown Area',
                 type: 'food' as const,
                 cost: 25,
                 duration: '1.5 hours',
              },
              afternoon: {
                 title: 'Museum Visit',
                 description: 'Explore the main museum to learn about local history and culture.',
                 location: 'National Museum',
                 type: 'activity' as const,
                 cost: 12,
                 duration: '2 hours',
              },
              dinner: {
                 title: 'Dinner Experience',
                 description: 'Enjoy dinner at a recommended restaurant with local specialties.',
                 location: 'Restaurant District',
                 type: 'food' as const,
                 cost: 35,
                 duration: '2 hours',
              },
           },
           {
              morning: {
                 title: 'Nature Exploration',
                 description:
                    'Visit a nearby natural attraction like a park, garden, or viewpoint.',
                 location: 'Natural Park',
                 type: 'activity' as const,
                 cost: 5,
                 duration: '3 hours',
              },
              lunch: {
                 title: 'Casual Lunch',
                 description: 'Have a casual lunch at a café or food market.',
                 location: 'Local Market',
                 type: 'food' as const,
                 cost: 15,
                 duration: '1 hour',
              },
              afternoon: {
                 title: 'Shopping & Exploring',
                 description: 'Browse local shops and boutiques for souvenirs and unique items.',
                 location: 'Shopping District',
                 type: 'activity' as const,
                 cost: 0,
                 duration: '2.5 hours',
              },
              dinner: {
                 title: 'Fine Dining Experience',
                 description: 'Enjoy a nicer dinner at a highly-rated restaurant.',
                 location: 'Fine Dining Area',
                 type: 'food' as const,
                 cost: 50,
                 duration: '2 hours',
              },
           },
        ];
     }
  };

  // Handle day change
  const handleDayChange = (index: number) => {
     setCurrentDayIndex(index);
  };

  // Handle activity type filter change
  const handleActivityTypeChange = (event: React.SyntheticEvent, newValue: string) => {
     setActivityType(newValue);
  };

  // Filter activities by type
  const getFilteredActivities = (activities: ItineraryActivity[]) => {
     if (activityType === 'all') return activities;
     return activities.filter((activity) => activity.type === activityType);
  };

  // Open edit dialog for an activity
  const handleEditActivity = (activity: ItineraryActivity) => {
     setEditActivity({ ...activity });
     setEditDialogOpen(true);
  };

  // Handle changes to activity in edit mode
  const handleActivityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
     if (!editActivity) return;

     const { name, value } = e.target;
     setEditActivity({
        ...editActivity,
        [name]: value,
     });
  };

  // Save edited activity
  const handleSaveActivity = () => {
     if (!editActivity) return;

     // Update itinerary
     setItinerary(
        itinerary.map((day) => ({
           ...day,
           activities: day.activities.map((activity) =>
              activity.id === editActivity.id ? editActivity : activity
           ),
        }))
     );

     setEditDialogOpen(false);
     setEditActivity(null);
  };

  // Remove activity from itinerary
  const handleRemoveActivity = (activityId: string) => {
     setItinerary(
        itinerary.map((day) => ({
           ...day,
           activities: day.activities.filter((activity) => activity.id !== activityId),
        }))
     );
  };

  // Add a new activity to a specific day
  const handleAddNewActivity = () => {
     if (currentDayIndex >= itinerary.length) return;

     const newActivity: ItineraryActivity = {
        id: `custom-${Date.now()}`,
        title: 'New Activity',
        description: 'Custom activity',
        time: '12:00 PM',
        location: tripDetails.destination,
        type: 'activity',
        isCustom: true,
     };

     onAddActivity(newActivity, itinerary[currentDayIndex].date);

     // Update local state
     const updatedItinerary = [...itinerary];
     updatedItinerary[currentDayIndex].activities.push(newActivity);
     setItinerary(updatedItinerary);
  };

  // Save the itinerary
  const handleSaveItinerary = () => {
     setLoading(true);

     try {
        onSaveItinerary(itinerary);
        setLoading(false);
     } catch (error) {
        console.error('Error saving itinerary:', error);
        setLoading(false);
     }
  };

  // Generate email for the itinerary
  const generateItineraryEmail = () => {
     let emailContent = `Itinerary for ${tripDetails.title} to ${tripDetails.destination}\n\n`;

     itinerary.forEach((day) => {
        emailContent += `${format(day.date, 'EEEE, MMMM d, yyyy')}\n`;
        emailContent += '-----------------------------------\n';

        day.activities.forEach((activity) => {
           emailContent += `${activity.time} - ${activity.title}\n`;
           emailContent += `Location: ${activity.location}\n`;
           if (activity.cost) emailContent += `Cost: $${activity.cost}\n`;
           if (activity.duration) emailContent += `Duration: ${activity.duration}\n`;
           emailContent += '\n';
        });

        emailContent += '\n';
     });

     // In a real app, you would send this to an email API

     alert('Itinerary email would be sent to all participants');
  };
  
  // Get icon for activity type
  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'activity':
        return <AttractionIcon />;
      case 'food':
        return <RestaurantIcon />;
      case 'accommodation':
        return <HotelIcon />;
      case 'transportation':
        return <TransportIcon />;
      default:
        return <ScheduleIcon />;
    }
  };
  
  // Initialize itinerary if empty
  const days = initializeItinerary();
  
  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">
            Trip Itinerary for {tripDetails.title}
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<MagicIcon />}
              onClick={generateItinerary}
              disabled={generating}
            >
              {generating ? 'Generating...' : 'Generate Itinerary'}
            </Button>
            
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<ShuffleIcon />}
              onClick={generateItinerary}
              disabled={generating}
            >
              Regenerate
            </Button>
          </Box>
        </Box>
        
        {generating ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <CircularProgress />
            <Typography variant="h6" sx={{ ml: 2 }}>
              Generating your perfect itinerary...
            </Typography>
          </Box>
        ) : (
          <>
            {/* Itinerary days tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={currentDayIndex} 
                onChange={(_, value) => handleDayChange(value)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {days.map((day, index) => (
                  <Tab 
                    key={index} 
                    label={format(day.date, 'EEE, MMM d')}
                    id={`day-tab-${index}`}
                    aria-controls={`day-panel-${index}`}
                  />
                ))}
              </Tabs>
            </Box>
            
            {/* Activity type filter */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs 
                value={activityType} 
                onChange={handleActivityTypeChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab value="all" label="All Activities" />
                <Tab value="activity" label="Activities" icon={<AttractionIcon />} />
                <Tab value="food" label="Food" icon={<RestaurantIcon />} />
                <Tab value="accommodation" label="Accommodations" icon={<HotelIcon />} />
                <Tab value="transportation" label="Transportation" icon={<TransportIcon />} />
              </Tabs>
            </Box>
            
            {/* Current day's activities */}
            {days.map((day, index) => (
              <Box
                key={index}
                role="tabpanel"
                hidden={currentDayIndex !== index}
                id={`day-panel-${index}`}
                aria-labelledby={`day-tab-${index}`}
              >
                {currentDayIndex === index && (
                  <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="h6">
                        {format(day.date, 'EEEE, MMMM d, yyyy')}
                      </Typography>
                      
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={handleAddNewActivity}
                      >
                        Add Activity
                      </Button>
                    </Box>
                    
                    <List>
                      {day.activities.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography variant="body1" color="text.secondary">
                            No activities planned for this day yet.
                          </Typography>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddNewActivity}
                            sx={{ mt: 2 }}
                          >
                            Add First Activity
                          </Button>
                        </Box>
                      ) : (
                        getFilteredActivities(day.activities).map((activity) => (
                          <ListItem
                            key={activity.id}
                            divider
                            secondaryAction={
                              <Box>
                                <IconButton 
                                  edge="end" 
                                  aria-label="edit"
                                  onClick={() => handleEditActivity(activity)}
                                >
                                  <EditIcon />
                                </IconButton>
                                <IconButton 
                                  edge="end" 
                                  aria-label="delete"
                                  onClick={() => handleRemoveActivity(activity.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Box>
                            }
                          >
                            <ListItemIcon>
                              {getActivityIcon(activity.type)}
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography variant="subtitle1">
                                    {activity.time} - {activity.title}
                                  </Typography>
                                  {activity.isCustom && (
                                    <Chip 
                                      label="Custom" 
                                      size="small" 
                                      color="secondary" 
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </Box>
                              }
                              secondary={
                                <>
                                  <Typography variant="body2" component="span">
                                    {activity.description}
                                  </Typography>
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" component="span">
                                      Location: {activity.location}
                                    </Typography>
                                    {activity.cost && (
                                      <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                                        Cost: ${activity.cost}
                                      </Typography>
                                    )}
                                    {activity.duration && (
                                      <Typography variant="body2" component="span" sx={{ ml: 2 }}>
                                        Duration: {activity.duration}
                                      </Typography>
                                    )}
                                  </Box>
                                </>
                              }
                            />
                          </ListItem>
                        ))
                      )}
                    </List>
                  </Box>
                )}
              </Box>
            ))}
            
            {/* Action buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={generateItineraryEmail}
                disabled={itinerary.length === 0 || itinerary.every(day => day.activities.length === 0)}
              >
                Email Itinerary
              </Button>
              
              <Box>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  sx={{ mr: 1 }}
                  disabled={itinerary.length === 0 || itinerary.every(day => day.activities.length === 0)}
                >
                  Download PDF
                </Button>
                
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveItinerary}
                  disabled={itinerary.length === 0 || itinerary.every(day => day.activities.length === 0)}
                >
                  Save Itinerary
                </Button>
              </Box>
            </Box>
          </>
        )}
      </Paper>
      
      {/* Edit Activity Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editActivity?.isCustom ? 'Edit Custom Activity' : 'Edit Activity'}
        </DialogTitle>
        <DialogContent>
          {editActivity && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Activity Title"
                  name="title"
                  value={editActivity.title}
                  onChange={handleActivityChange}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Time"
                  name="time"
                  value={editActivity.time}
                  onChange={handleActivityChange}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={editActivity.description}
                  onChange={handleActivityChange}
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Location"
                  name="location"
                  value={editActivity.location}
                  onChange={handleActivityChange}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Cost"
                  name="cost"
                  type="number"
                  value={editActivity.cost || ''}
                  onChange={handleActivityChange}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              </Grid>
              
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Duration"
                  name="duration"
                  value={editActivity.duration || ''}
                  onChange={handleActivityChange}
                  placeholder="2 hours"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  name="notes"
                  value={editActivity.notes || ''}
                  onChange={handleActivityChange}
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveActivity} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ItineraryGenerator;
